/**
 * Hook: usePipelineBoard
 *
 * Busca dados completos de uma pipeline com stages e cards
 * para visualização em Kanban. Visibilidade controlada via RLS.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { KanbanBoard, Pipeline, StageWithCards, MoveCardRequest, PipelinePerfilUsuario, PipelineStage, PipelineCard } from '../types';
import { pipelineBoardKey } from './queryKeys';

import { getProductionPeriod } from '@/utils/productionPeriod';

export function usePipelineBoard(
  pipelineId: string,
  productionPeriodFilter?: { month: number; year: number } | null,
  startDay: number = 1
) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const boardQueryKey = [...pipelineBoardKey(pipelineId), profile?.uuid, productionPeriodFilter, startDay];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: boardQueryKey,
    queryFn: async () => {
      if (!profile?.tenant_id || !pipelineId) {
        throw new Error('Tenant ID ou Pipeline ID não encontrado');
      }

      return await fetchPipelineBoard(pipelineId, productionPeriodFilter, startDay);
    },
    enabled: !!profile?.tenant_id && !!pipelineId,
    staleTime: 0,
    retry: 2
  });

  const moveCardMutation = useMutation({
    mutationFn: async (request: Omit<MoveCardRequest, 'usuarioId'>) => {
      if (!profile?.uuid) throw new Error('Usuário não autenticado');
      return await moveCard({ ...request, usuarioId: profile.uuid });
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: boardQueryKey });

      const previousBoard = queryClient.getQueryData<KanbanBoard>(boardQueryKey);
      if (previousBoard) {
        queryClient.setQueryData<KanbanBoard>(
          boardQueryKey,
          moveCardOptimistically(previousBoard, request.cardId, request.stageId)
        );
      }

      return { previousBoard };
    },
    onError: (_err, _request, context) => {
      if (context?.previousBoard) {
        queryClient.setQueryData(boardQueryKey, context.previousBoard);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineBoardKey(pipelineId) });
      queryClient.invalidateQueries({ queryKey: ['client-journey'] });
    }
  });

  return {
    kanbanBoard: data,
    loading: isLoading,
    error: error as Error | null,
    refetch,
    moveCard: moveCardMutation.mutate,
    moveCardAsync: moveCardMutation.mutateAsync,
    isMovingCard: moveCardMutation.isPending
  };
}

/** Move o card entre stages no cache local, sem esperar a resposta do servidor */
function moveCardOptimistically(
  board: KanbanBoard,
  cardId: string,
  targetStageId: string
): KanbanBoard {
  let movedCard: PipelineCard | undefined;

  const stagesWithoutCard = board.stages.map((swc) => {
    const card = swc.cards.find((c) => c.id === cardId);
    if (!card) return swc;

    movedCard = card;
    const cards = swc.cards.filter((c) => c.id !== cardId);
    return { ...swc, cards, count: cards.length };
  });

  if (!movedCard) return board;

  const updatedCard: PipelineCard = { ...movedCard, stage_id: targetStageId };

  const stages = stagesWithoutCard.map((swc) => {
    if (swc.stage.id !== targetStageId) return swc;
    const cards = [...swc.cards, updatedCard].sort(
      (a, b) =>
        new Date(b.data_criacao ?? b.created_at ?? 0).getTime() -
        new Date(a.data_criacao ?? a.created_at ?? 0).getTime()
    );
    return { ...swc, cards, count: cards.length };
  });

  return { ...board, stages };
}

async function fetchPipelineBoard(
  pipelineId: string,
  productionPeriodFilter?: { month: number; year: number } | null,
  startDay: number = 1
): Promise<KanbanBoard> {
  const { data: pipelineData, error: pipelineError } = await supabase
    .from('pipelines')
    .select('*')
    .eq('id', pipelineId)
    .single();

  if (pipelineError || !pipelineData) {
    throw new Error('Pipeline não encontrada');
  }

  const pipeline: Pipeline = {
    ...pipelineData,
    perfil_usuario: pipelineData.perfil_usuario as PipelinePerfilUsuario | PipelinePerfilUsuario[],
    tenant_ids: pipelineData.tenant_ids as number[] | undefined,
  } as Pipeline;

  const { data: stagesData, error: stagesError } = await supabase
    .from('pipeline_stages')
    .select('*')
    .eq('pipeline_id', pipelineId)
    .order('ordem', { ascending: true });

  if (stagesError) {
    throw new Error('Erro ao buscar stages');
  }

  let cardsQuery = supabase
    .from('pipeline_cards')
    .select(`
      *,
      contratos(
        id,
        valor_bem,
        status,
        grupo_id,
        bem_id,
        cliente_id,
        cota,
        grupos(
          numero_grupo,
          especie,
          titulo,
          data_proxima_assembleia,
          data_vencimento_grupo
        ),
        bens(
          codigo_bem,
          tipo_bem
        ),
        clientes(
          nome
        )
      )
    `)
    .eq('pipeline_id', pipelineId)
    .eq('cancelado', false)
    .order('data_criacao', { ascending: false });

  if (productionPeriodFilter) {
    const { startDate, endDate } = getProductionPeriod(
      productionPeriodFilter.month,
      productionPeriodFilter.year,
      startDay
    );
    cardsQuery = cardsQuery
      .gte('data_criacao', startDate.toISOString())
      .lte('data_criacao', endDate.toISOString());
  }

  const { data: cardsData, error: cardsError } = await cardsQuery;

  if (cardsError) {
    throw new Error('Erro ao buscar cards');
  }

  const stagesWithCards: StageWithCards[] = (stagesData || []).map(stage => {
    const cards = (cardsData || []).filter(card => card.stage_id === stage.id);
    return {
      stage: {
        ...stage,
        campos_obrigatorios: stage.campos_obrigatorios as string[] | undefined,
        visibilidade: stage.visibilidade as any,
        oculta: Boolean((stage as { oculta?: boolean }).oculta),
      } as PipelineStage,
      cards: cards as PipelineCard[],
      count: cards.length
    };
  });

  return {
    pipeline,
    stages: stagesWithCards,
    totalCards: cardsData?.length || 0,
    lastUpdated: new Date()
  };
}

async function moveCard(request: MoveCardRequest): Promise<void> {
  const { cardId, stageId, stagAnteriorId, motivo, usuarioId } = request;

  const { error: updateError } = await supabase
    .from('pipeline_cards')
    .update({
      stage_id: stageId,
      data_ultima_atualizacao: new Date().toISOString()
    })
    .eq('id', cardId);

  if (updateError) {
    throw new Error(`Erro ao mover card: ${updateError.message}`);
  }

  const { error: historyError } = await supabase
    .from('card_history')
    .insert({
      card_id: cardId,
      usuario_id: usuarioId,
      acao: 'movido_para_stage',
      stage_anterior_id: stagAnteriorId,
      stage_novo_id: stageId,
      motivo
    });

  if (historyError) {
    console.error('Erro ao registrar histórico:', historyError);
  }
}

export function getStageColor(stage: { nome: string; cor?: string }): string {
  if (stage.cor) {
    return `border-[${stage.cor}]`;
  }

  const colors: Record<string, string> = {
    'Novo Lead': 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',
    'Contato 1': 'bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800',
    'Contato 2': 'bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800',
    'Contato 3': 'bg-pink-50 dark:bg-pink-950/20 border-pink-200 dark:border-pink-800',
    'Contato 4': 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800',
    'Perdido': 'bg-gray-50 dark:bg-gray-950/20 border-gray-200 dark:border-gray-800',
    'Interação': 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800',
    'Agendado': 'bg-cyan-50 dark:bg-cyan-950/20 border-cyan-200 dark:border-cyan-800',
    'Venda Finalizada': 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800',
  };

  return colors[stage.nome] || 'bg-slate-50 dark:bg-slate-950/20 border-slate-200 dark:border-slate-800';
}
